import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useEffect, useState } from 'react';
import useUserContext from 'Context/UserContext';
import useFormInput from 'Components/UI/BehavioralFormElement/Helpers';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { NotificationManager } from 'react-notifications';
import Drawer from 'Components/Ant/Drawer';
import Select, { Option } from 'Components/Ant/Select';
import Text from 'Components/UI/BehavioralFormElement/Text/Input';
import Email from 'Components/UI/BehavioralFormElement/Text/Email';
import SquareButton from 'Components/UI/SquareButton';
import { addQueryWithReplace } from 'Helpers/Query';
import closeModal from 'Helpers/ModalHelper';
import { sortByValue } from 'Helpers/Formats';
import './index.css';
import RadioGroup from 'Components/UI/BehavioralFormElement/RadioGroup';

function CreateHouseholdFlyout() {
  const { createHousehold, fetchHouseholdsMeta } = useSwaggerlessContext();
  const { userResource } = useUserContext();
  const { refreshHouseholdSearch } = useSimpleHousehold();
  const agentId = userResource?.attr('agentId');

  const firstName = useFormInput('');
  const lastName = useFormInput('');
  const email = useFormInput('');
  const spouseClientFirstName = useFormInput('');
  const spouseClientLastName = useFormInput('');
  const spouseClientEmail = useFormInput('');
  const addressLine1 = useFormInput('');
  const addressLine2 = useFormInput('');
  const addressLine3 = useFormInput('');
  const city = useFormInput('');
  const zip = useFormInput('');
  const state = useFormInput('');
  const maritalStatus = useFormInput('');

  const [csm, setCSM] = useState('0');
  const [leadPlanner, setLeadPlanner] = useState('0');
  const [source, setSource] = useState('0');
  const [sourceType, setSourceType] = useState('0');
  const [sourcePartner, setSourcePartner] = useState('0');
  const [sourceTypes, setSourceTypes] = useState([]);
  const [sourcePartners, setSourcePartners] = useState([]);
  const [isTestHousehold, setIsTestHousehold] = useState('false');
  const [leadPlanners, setLeadPlanners] = useState([]);
  const [csms, setCsms] = useState([]);
  const [meta, setMeta] = useState(null);

  function getFieldName(name) {
    let def = meta.meta.template.filter(a => a.name === name);
    def = def.length ? def[0] : { options: [] };
    return def;
  }

  function getDependentOptions(name, val) {
    const def = getFieldName(name);
    const options = (def.options || [])
      .filter(
        o => o.depends && o.depends.length && o.depends.some(v => v === val),
      )
      .map(o => ({
        id: o.id,
        value: o.prompt,
      }));
    options.unshift({ id: '0', value: '' });
    return options;
  }

  useEffect(() => {
    if (!meta) {
      return;
    }
    const lps = getDependentOptions('leadPlannerId', isTestHousehold.toString());
    setLeadPlanners(lps);
    const cs = getDependentOptions('csManagerId', isTestHousehold.toString());
    setCsms(cs);
    const aIdString = agentId.toString();
    const lp = lps.filter(({ id }) => id === aIdString);
    const c = cs.filter(({ id }) => id === aIdString);
    if (lp.length > 0) {
      setLeadPlanner(aIdString);
    }
    if (c.length > 0) {
      setCSM(aIdString);
    }
  }, [agentId, meta]);

  useEffect(() => {
    fetchHouseholdsMeta().then(res => setMeta(res.data[0]));
  }, []);

  if (!meta) {
    return null;
  }

  function getOptions(name) {
    const def = getFieldName(name);
    const options = (def.options || []).map(o => ({
      id: o.id,
      value: o.prompt,
    }));

    options.unshift({ id: '0', value: '' });

    return options;
  }

  const sources = getOptions('sourceId');

  function updateSource(val) {
    setSource(val);
    if (+val > 0) {
      let options = getDependentOptions('sourceTypeId', val);
      setSourceTypes(options);
      setSourceType('0');

      options = getDependentOptions('sourcePartnerId', val);
      setSourcePartners(options);
      setSourcePartner('0');
    }
  }

  function save() {
    const hh = {
      type: 'household',
      attributes: {
        primaryClientFirstName: firstName.value,
        primaryClientLastName: lastName.value,
        primaryClientEmail: email.value,
        statusId: '1', // prospect
        sourceId: source !== '0' ? source.toString() : '',
        sourceTypeId: sourceType !== '0' ? sourceType.toString() : '',
        sourcePartnerId: sourcePartner !== '0' ? sourcePartner.toString() : '',
        csManagerId: csm !== '0' ? csm.toString() : '',
        leadPlannerId: leadPlanner !== '0' ? leadPlanner.toString() : '',
        communicationType: 'online',
        addressLine1: addressLine1.value,
        addressLine2: addressLine2.value,
        addressLine3: addressLine3.value,
        city: city.value,
        state: state.value,
        postalCode: zip.value,
        spouseClientFirstName: spouseClientFirstName.value,
        spouseClientLastName: spouseClientLastName.value,
        maritalStatus: maritalStatus.value,
        escalationType: 'notEscalated',
        isTestHousehold,
      },
    };
    if (spouseClientEmail.value) {
      hh.attributes.spouseClientEmail = spouseClientEmail.value;
    }
    createHousehold(hh)
      .then(() => {
        addQueryWithReplace({ refresh: 'createHousehold' });
        closeModal();
        NotificationManager.success('Household created.');
        return refreshHouseholdSearch();
      })
      .catch((resp) => {
        if (resp && resp.errors) {
          const errs = resp.errors;
          displayErrorMessage(errs, errs);
        } else {
          displayErrorMessage(resp, 'An unknown error occurred');
        }
      });
  }

  const stateOptions = getOptions('state');
  const maritalOptions = getOptions('maritalStatus');
  const hasSpouse = maritalStatus.value === 'significantOther'
    || maritalStatus.value === 'married';

  return (
    <Drawer
      className="create-household-flyout"
      visible
      width={500}
      onClose={closeModal}
    >
      <div className="title book">Add New Client</div>
      <hr />
      <div className="columns">
        <div className="client-info">
          <div className="info-header">Client</div>
          <Text
            name="firstName"
            id="firstName"
            value={firstName.value}
            placeholder="First Name"
            label="First Name"
            onChange={(n, v) => firstName.onChange(v)}
          />
          <Text
            name="lastName"
            value={lastName.value}
            placeholder="Last Name"
            label="Last Name"
            onChange={(n, v) => lastName.onChange(v)}
          />
          <Email
            name="Email"
            value={email.value}
            placeholder="Email"
            label="Email"
            onChange={(n, v) => email.onChange(v)}
          />
          <Select
            className="marital-status-selector"
            showSearch
            labelInValue
            placeholder="Marital Status"
            filterOption={(input, option) => option.props.children
              .toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(val) => {
              maritalStatus.onChange(val.key);
            }}
          >
            {sortByValue(maritalOptions).map(d => (
              <Option key={d.id} value={d.id}>
                {d.value}
              </Option>
            ))}
          </Select>
        </div>
        {hasSpouse && (
          <div className="spouse-info">
            <div className="info-header">Spouse</div>
            <Text
              name="spouseClientFirstName"
              id="spouseClientFirstName"
              value={spouseClientFirstName.value}
              placeholder="Spouse First Name"
              label="Spouse First Name"
              onChange={(n, v) => spouseClientFirstName.onChange(v)}
            />
            <Text
              name="spouseClientLastName"
              value={spouseClientLastName.value}
              placeholder="Spouse Last Name"
              label="Last Name"
              onChange={(n, v) => spouseClientLastName.onChange(v)}
            />
            <Email
              name="spouseClientEmail"
              value={spouseClientEmail.value}
              placeholder="Spouse Email"
              label="Spouse Email"
              onChange={(n, v) => spouseClientEmail.onChange(v)}
            />
          </div>
        )}
      </div>
      <div>
        <div className="horizontal">
          <div>Test Client</div>
          <div className="input">
            <RadioGroup
              test="test-household"
              onChange={(_, isChecked) => setIsTestHousehold(isChecked)}
              name="isTestHousehold"
              options={[
                { id: 'true', text: 'Yes' },
                { id: 'false', text: 'No' },
              ]}
              value={isTestHousehold}
            />
          </div>
        </div>
        <div className="horizontal">
          <div>CSM</div>
          <Select
            showSearch
            labelInValue
            placeholder="Select CSM"
            filterOption={(input, option) => option.props.children
              .toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(val) => {
              setCSM(val.key);
            }}
            value={csm === '0' ? undefined : { key: csm }}
          >
            {sortByValue(csms).map(d => (
              <Option key={d.id} value={d.id}>
                {d.value}
              </Option>
            ))}
          </Select>
        </div>
        <div className="horizontal">
          <div>Lead Planner</div>
          <Select
            showSearch
            labelInValue
            placeholder="Select Lead Planner"
            filterOption={(input, option) => option.props.children
              .toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(val) => {
              setLeadPlanner(val.key);
            }}
            value={leadPlanner === '0' ? undefined : { key: leadPlanner }}
          >
            {sortByValue(leadPlanners).map(d => (
              <Option key={d.id} value={d.id}>
                {d.value}
              </Option>
            ))}
          </Select>
        </div>
        <div className="horizontal">
          <div>Partner</div>
          <Select
            showSearch
            labelInValue
            placeholder="Select Source"
            filterOption={(input, option) => option.props.children
              .toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(val) => {
              updateSource(val.key);
            }}
          >
            {sortByValue(sources).map(d => (
              <Option key={d.id} value={d.id}>
                {d.value}
              </Option>
            ))}
          </Select>
        </div>
        <div className="horizontal">
          <div>Type</div>
          <Select
            showSearch
            labelInValue
            placeholder="Select Type"
            filterOption={(input, option) => option.props.children
              .toLowerCase()
              .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(val) => {
              setSourceType(val.key);
            }}
          >
            {sortByValue(sourceTypes).map(d => (
              <Option key={d.id} value={d.id}>
                {d.value}
              </Option>
            ))}
          </Select>
        </div>
        {sourcePartners.length > 1 && (
          <div className="horizontal">
            <div>Contact</div>
            <Select
              showSearch
              labelInValue
              placeholder="Select Contact"
              filterOption={(input, option) => option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
              }
              onChange={(val) => {
                setSourcePartner(val.key);
              }}
            >
              {sortByValue(sourcePartners).map(d => (
                <Option value={d.id}>{d.value}</Option>
              ))}
            </Select>
          </div>
        )}
        <hr />
        <div className="contact-info">Contact Information</div>
        <Text
          name="addressLine1"
          id="addressLine1"
          value={addressLine1.value}
          placeholder="Address Line 1"
          label="Address Line 1"
          onChange={(n, v) => addressLine1.onChange(v)}
        />
        <Text
          name="addressLine2"
          id="addressLine2"
          value={addressLine2.value}
          placeholder="Address Line 2"
          label="Address Line 2"
          onChange={(n, v) => addressLine2.onChange(v)}
        />
        <Text
          name="addressLine3"
          id="addressLine3"
          value={addressLine3.value}
          placeholder="Address Line 3"
          label="Address Line 3"
          onChange={(n, v) => addressLine3.onChange(v)}
        />
        <Text
          name="city"
          id="city"
          value={city.value}
          placeholder="City"
          label="City"
          onChange={(n, v) => city.onChange(v)}
        />
      </div>
      <div className="columns">
        <Select
          className="state-selector"
          showSearch
          labelInValue
          placeholder="Select State"
          filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase())
            >= 0
          }
          onChange={(val) => {
            state.onChange(val.key);
          }}
        >
          {sortByValue(stateOptions).map(d => (
            <Option key={d.id} value={d.id}>
              {d.value}
            </Option>
          ))}
        </Select>
        <Text
          name="zip"
          id="zip"
          value={zip.value}
          placeholder="Zip"
          maxLength={5}
          label="Zip"
          onChange={(n, v) => zip.onChange(v)}
        />
      </div>
      <SquareButton test="add-client-flyout-btn" color="blue" onClick={save} text="Add Client" />
    </Drawer>
  );
}

CreateHouseholdFlyout.propTypes = {
};

const CreateHouseholdFlyoutWrapper = CreateHouseholdFlyout;

export default CreateHouseholdFlyoutWrapper;
